/**
 * Main Chat Application Component
 * Orchestrates the chat interface with AI agent integration
 */
import {
  useCallback,
  useState
} from "react";
import {
  redactDebugPayload,
  type DebugTraceEvent
} from "@/shared/types/debug-trace";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useAgentAuthSession } from "@/hooks/useAgentAuthSession";
import { useAuthClaims } from "@/hooks/useAuth0Permissions";
import { useDebugTrace } from "@/hooks/useDebugTrace";
import { AuthenticatedChat } from "@/components/chat/AuthenticatedChat";

/**
 * Main Chat component
 */
export default function Chat() {
  const [isDebugDrawerOpen, setIsDebugDrawerOpen] = useState(false);
  const authSession = useAgentAuthSession();
  const {
    events,
    appendEvent: appendDebugEvent,
    clearEvents: clearDebugEvents
  } = useDebugTrace();
  const { isEnabled } = useFeatureFlags();
  const { permissions, hasPermission } = useAuthClaims();
  const canOpenActivityMentions =
    permissions.includes("contact:view") ||
    permissions.includes("admin:all") ||
    permissions.includes("admin:global");

  const hasDebugPermission = hasPermission("admin:global");

  const isDevelopment =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  const showDebug = hasDebugPermission || isDevelopment;
  const debugCaptureEnabled = showDebug && isDebugDrawerOpen;

  const emitClientDebugEvent = useCallback(
    (event: Partial<DebugTraceEvent>) => {
      if (!debugCaptureEnabled) {
        return;
      }

      appendDebugEvent({
        ...event,
        source: event.source ?? "client",
        payload: redactDebugPayload(event.payload)
      });
    },
    [appendDebugEvent, debugCaptureEnabled]
  );

  const sessionName = authSession.sessionName;

  if (authSession.isAuthorizing) {
    return (
      <div className="w-full h-screen flex flex-col">
        <div className="flex items-center justify-center flex-1">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">
              Authorizing agent session...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionName) {
    return (
      <div className="w-full h-screen flex flex-col">
        <div className="flex items-center justify-center flex-1">
          <div className="text-center space-y-4 max-w-md px-4">
            <p className="text-red-500">
              {authSession.authErrorMessage
                ? "Authentication Error"
                : "Authentication Required"}
            </p>
            <p className="text-muted-foreground text-sm">
              {authSession.authErrorMessage ||
                "Please log in to use the ClaimMosaic Agent."}
            </p>
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
              onClick={() => void authSession.login()}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthenticatedChat
      sessionName={sessionName}
      showDebug={showDebug}
      debugCaptureEnabled={debugCaptureEnabled}
      isDebugDrawerOpen={isDebugDrawerOpen}
      onToggleDebugDrawer={() => setIsDebugDrawerOpen((prev) => !prev)}
      onSetDebugDrawerOpen={setIsDebugDrawerOpen}
      debugEvents={events}
      clearDebugEvents={clearDebugEvents}
      emitClientDebugEvent={emitClientDebugEvent}
      isEnabled={isEnabled}
      accessToken={authSession.accessToken}
      tokenLoading={authSession.tokenLoading}
      tokenError={authSession.tokenError}
      getAccessToken={authSession.getAccessToken}
      onLogin={authSession.login}
      canOpenActivityMentions={canOpenActivityMentions}
    />
  );
}
